import { Reveal, MaskLines } from "@/components/motion";
import { Ribbons, Atmosphere, Sparkle } from "@/components/Art";
import { Cta } from "@/components/Cta";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export function SectionHead({ eyebrow, title, intro, light = false, className = "", align = "left" }) {
  return (
    <div className={`max-w-3xl ${align === "center" ? "mx-auto text-center" : ""} ${className}`}>
      {eyebrow && (
        <Reveal className={`eyebrow mb-5 flex items-center gap-3 ${align === "center" ? "justify-center" : ""} ${light ? "!text-si-blue" : ""}`}>
          <span className={`h-px w-8 ${light ? "bg-si-blue" : "bg-si-cyan"}`} />{eyebrow}
        </Reveal>
      )}
      <MaskLines lines={Array.isArray(title) ? title : [title]} className={`h-section ${light ? "text-si-ink" : ""}`} />
      {intro && <Reveal delay={0.15} className={`mt-6 text-base sm:text-lg leading-relaxed ${light ? "text-[#475467]" : "text-si-muted"}`}>{intro}</Reveal>}
    </div>
  );
}

export function PageHero({ eyebrow, title, intro, children, testId = "page-hero" }) {
  return (
    <section className="relative overflow-hidden pb-16 pt-36 sm:pb-24 sm:pt-44" data-testid={testId}>
      <Atmosphere />
      <div className="bg-grid pointer-events-none absolute inset-0 opacity-60" aria-hidden="true" />
      <Ribbons id={`ph-${testId}`} className="pointer-events-none absolute inset-x-0 bottom-0 h-[70%] w-full opacity-60" />
      <div className="wrap relative">
        <Reveal className="eyebrow mb-6 flex items-center gap-3"><Sparkle className="h-3.5 w-3.5" />{eyebrow}</Reveal>
        <MaskLines as="h1" onLoad lines={Array.isArray(title) ? title : [title]} className="max-w-5xl text-[clamp(2.4rem,6vw,5rem)] font-extrabold leading-[1.0] tracking-[-0.035em]" testId={`${testId}-title`} />
        {intro && <Reveal delay={0.3} className="mt-8 max-w-2xl text-base sm:text-lg leading-relaxed text-si-muted">{intro}</Reveal>}
        {children && <Reveal delay={0.45} className="mt-10 flex flex-wrap gap-3">{children}</Reveal>}
      </div>
    </section>
  );
}

export function CtaBand({ title = "The next great product starts with a conversation.", intro = "Tell us what you're building, where you're stuck, or what you're exploring. We'll work with you to identify the next practical step.", primary = "Start a Conversation", primaryTo = "/contact", secondary = "Explore Our Ventures", secondaryTo = "/ventures", testId = "cta-band" }) {
  return (
    <section className="relative overflow-hidden py-24 sm:py-36" data-testid={testId}>
      <div className="wrap">
        <div className="relative overflow-hidden rounded-[28px] border border-si-line bg-[#071027] px-6 py-16 sm:px-14 sm:py-24">
          <div className="pointer-events-none absolute -left-20 -top-24 h-80 w-80 rounded-full bg-si-cyan/25 blur-[90px]" aria-hidden="true" />
          <div className="pointer-events-none absolute -bottom-28 right-0 h-80 w-96 rounded-full bg-si-orange/20 blur-[100px]" aria-hidden="true" />
          <Ribbons id={`cb-${testId}`} className="pointer-events-none absolute inset-0 h-full w-full opacity-50" />
          <div className="relative max-w-3xl">
            <MaskLines lines={[title]} className="text-[clamp(2.1rem,5vw,4.2rem)] font-extrabold leading-[1.02] tracking-[-0.035em]" />
            <Reveal delay={0.15} className="mt-6 max-w-xl text-base sm:text-lg text-si-muted">{intro}</Reveal>
            <Reveal delay={0.25} className="mt-10 flex flex-wrap gap-3">
              <Cta to={primaryTo} size="lg" testId={`${testId}-primary`} trackId={`${testId}-primary`}>{primary}</Cta>
              {secondary && <Cta to={secondaryTo} variant="secondary" size="lg" testId={`${testId}-secondary`}>{secondary}</Cta>}
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

export function FaqList({ items, testId = "faq" }) {
  return (
    <Accordion type="single" collapsible className="border-t border-si-line" data-testid={testId}>
      {items.map(([q, a], i) => (
        <AccordionItem key={q} value={`q${i}`} className="border-b border-si-line">
          <AccordionTrigger data-testid={`${testId}-q-${i}`}
            className="group py-6 text-left text-lg font-semibold hover:no-underline sm:text-xl [&>svg]:text-si-cyan">
            <span className="flex gap-5"><span className="font-mono text-xs text-si-cyan/80 pt-1.5">{String(i + 1).padStart(2, "0")}</span>{q}</span>
          </AccordionTrigger>
          <AccordionContent className="pb-6 pl-10 text-base leading-relaxed text-si-muted">{a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

export function BulletList({ items, light = false }) {
  return (
    <ul className="space-y-3">
      {items.map((t) => (
        <li key={t} className={`flex gap-3 text-base ${light ? "text-[#344054]" : "text-si-muted"}`}>
          <Sparkle className="mt-1.5 h-3 w-3 shrink-0" color={light ? "#176BFF" : "#17B6F4"} />{t}
        </li>
      ))}
    </ul>
  );
}
